import { Configuration, RetailersApi } from '@/api';
import { fetchAllPaginated } from '@/api/utils';
import SelectBox, {
  Item,
  MultiSelectProps,
  OtherSelectBoxProps,
  SingleSelectProps,
} from '@/components/select/SelectBox';
import { Retailer, RetailerAssociation } from '@/models/Retailer';
import { useAuth } from '@/modules/auth/contexts/AuthContext';

type RetailerSelectBoxProps<T extends Item> =
  | (SingleSelectProps<T> &
      OtherSelectBoxProps<T> & {
        multiple?: false;
        showOrganization?: true;
      })
  | (MultiSelectProps<T> &
      OtherSelectBoxProps<T> & {
        multiple: true;
        showOrganization?: true;
      });

const formatRetailerName = (
  customer: { name: string; organization?: { name: string } },
  showOrganization?: boolean,
) => {
  return showOrganization && customer.organization
    ? `${customer.name} — Organization: ${customer.organization.name}`
    : customer.name;
};

const RetailerSelectBox = ({ showOrganization, ...props }: RetailerSelectBoxProps<Retailer>) => {
  const retailersApi = new RetailersApi(new Configuration(useAuth));

  const listRetailers = async (): Promise<Retailer[]> => {
    const customers = await fetchAllPaginated<Retailer>(
      (offset, limit) =>
        retailersApi.listRetailers(offset, limit, [RetailerAssociation.ORGANIZATION]),
      100,
      1000,
    );

    return customers.map((customer) => ({
      ...customer,
      name: formatRetailerName(customer, showOrganization),
    }));
  };

  const { label = 'Retailer:', placeholder = 'Select retailer' } = props;

  return (
    <SelectBox<Retailer>
      {...props}
      name="retailer"
      label={label}
      placeholder={placeholder}
      queryFn={listRetailers}
      queryKey={['retailer', showOrganization ? 'withOrganization' : '']}
    />
  );
};

export default RetailerSelectBox;
